import { z } from 'zod';
import { ARCADE_GAME_IDS, MAX_ARCADE_FRAMES, MAX_ARCADE_INPUTS } from './arcade/index.js';
import { lookSchema } from './look.js';

export * from './arcade/index.js';

/**
 * WebSocket messages for arcade mini-games (single-player score runs and
 * real-time multiplayer races). Owned by the arcade feature. Every type
 * starts with `arcade:`.
 *
 * Single-player runs are verified by replay: the server hands out a seed, the
 * client plays and sends back its input log, and the server re-simulates.
 */

export const arcadeGameIdSchema = z.enum(ARCADE_GAME_IDS);

const itemId = z.string().min(1).max(64);

export const arcadeClientMessageSchema = z.discriminatedUnion('t', [
  /** Start a run at a machine in the current room. */
  z.object({ t: z.literal('arcade:start'), game: arcadeGameIdSchema, itemId }),
  /** Finish a run: the full input log and the score the client saw. */
  z.object({
    t: z.literal('arcade:finish'),
    runId: z.string().min(1).max(64),
    inputs: z
      .array(z.tuple([z.number().int().min(0).max(MAX_ARCADE_FRAMES), z.number().int().min(0).max(255)]))
      .max(MAX_ARCADE_INPUTS),
    score: z.number().int().min(0).max(10_000_000),
  }),
  z.object({ t: z.literal('arcade:abandon'), runId: z.string().min(1).max(64) }),

  /** Dragon boat: watch a lobby, join/leave it, toggle ready, paddle. */
  z.object({ t: z.literal('arcade:boat_open'), itemId }),
  z.object({ t: z.literal('arcade:boat_close'), itemId }),
  z.object({ t: z.literal('arcade:boat_join'), itemId }),
  z.object({ t: z.literal('arcade:boat_leave'), itemId }),
  z.object({ t: z.literal('arcade:boat_ready'), itemId, ready: z.boolean() }),
  z.object({ t: z.literal('arcade:paddle'), itemId }),
]);

export const racerSchema = z.object({
  userId: z.string(),
  name: z.string(),
  look: lookSchema,
  ready: z.boolean(),
  /** 0..RACE_DISTANCE */
  progress: z.number(),
  /** Units per second. */
  speed: z.number(),
  strokes: z.number().int(),
  strongStrokes: z.number().int(),
  /** Whether the most recent stroke landed on the beat (for the splash effect). */
  lastStrong: z.boolean().nullable(),
  lastStrokeAt: z.number().nullable(),
  place: z.number().int().nullable(),
  finishedAt: z.number().nullable(),
});
export type Racer = z.infer<typeof racerSchema>;

export const raceStateSchema = z.object({
  itemId: z.string(),
  phase: z.enum(['lobby', 'countdown', 'racing', 'finished']),
  racers: z.array(racerSchema),
  /** Lobby countdown end, server time. */
  countdownEndsAt: z.number().nullable(),
  /** When the paddling starts (after the intro); beats fall every beatMs after it. */
  startAt: z.number().nullable(),
  beatMs: z.number(),
  serverTime: z.number(),
  /** Winner's reward, when paid. */
  reward: z.object({ userId: z.string(), amount: z.number().int() }).nullable(),
});
export type RaceState = z.infer<typeof raceStateSchema>;

export const arcadeServerMessageSchema = z.discriminatedUnion('t', [
  /** A run may begin: play this game with this seed. */
  z.object({
    t: z.literal('arcade:run'),
    runId: z.string(),
    game: arcadeGameIdSchema,
    seed: z.number().int().min(0).max(0xffffffff),
  }),
  /** The verdict on a finished run. */
  z.object({
    t: z.literal('arcade:result'),
    runId: z.string(),
    game: arcadeGameIdSchema,
    accepted: z.boolean(),
    score: z.number().int(),
    reward: z.number().int(),
    best: z.number().int(),
    personalBest: z.boolean(),
    /** Why a run was refused, or why it paid nothing. */
    note: z.string().nullable(),
  }),
  z.object({ t: z.literal('arcade:race'), state: raceStateSchema }),
  /** A lobby went away (the boat was picked up or the room emptied). */
  z.object({ t: z.literal('arcade:closed'), itemId: z.string() }),
]);

export type ArcadeClientMessage = z.infer<typeof arcadeClientMessageSchema>;
export type ArcadeServerMessage = z.infer<typeof arcadeServerMessageSchema>;

/** One row of an arcade leaderboard (GET /api/arcade/:game/scores). */
export interface ArcadeScoreEntry {
  userId: string;
  displayName: string;
  look: z.infer<typeof lookSchema>;
  score: number;
}

export interface ArcadeScoresResponse {
  game: (typeof ARCADE_GAME_IDS)[number];
  today: ArcadeScoreEntry[];
  allTime: ArcadeScoreEntry[];
  friends: ArcadeScoreEntry[];
  best: number | null;
  earnedToday: number;
  dailyCap: number;
}
